/**
 * PDF Generator for EVAT Mini ESG™ Reports
 * Uses browser-based PDF generation (no server-side dependencies)
 */

import { useMiniESGStore } from '../store/miniESGStore';
import QRCode from 'qrcode';
import i18n from '../i18n';

export async function generateMiniESGPDF(): Promise<Blob> {
  const store = useMiniESGStore.getState();
  const { companyName, taxId, score, answers, recommendations, reportDate, savedReportId, qrCodeUrl } = store;

  // Branding configuration (can be set via environment variables)
  const brand = {
    name: process.env.MINI_ESG_BRAND_NAME || 'EVATlabs ESG Engine',
    primary: process.env.MINI_ESG_PDF_PRIMARY_COLOR || '#00ff99',
    logoUrl: process.env.MINI_ESG_PDF_LOGO_URL || '',
    contactName: process.env.MINI_ESG_CONTACT_NAME || 'EVATlabs',
    contactEmail: process.env.MINI_ESG_CONTACT_EMAIL || 'hello@evatlabs.org',
    contactPhone: process.env.MINI_ESG_CONTACT_PHONE || ''
  };
  
  // Get current language for PDF content
  const lang = i18n.language || 'en';
  const t = (key: string, defaultValue: string) => {
    try {
      return i18n.t(key) || defaultValue;
    } catch {
      return defaultValue;
    }
  };
  
  // PDF text labels based on current language
  const labels = {
    subtitle: lang === 'hu' ? 'Fenntarthatósági értékelési jelentés' : 'Sustainability Assessment Report',
    unnamedCompany: lang === 'hu' ? 'Névtelen cég' : 'Unnamed Company',
    taxIdLabel: lang === 'hu' ? 'Adószám' : 'Tax ID',
    dateLabel: lang === 'hu' ? 'Dátum' : 'Date',
    levelRed: lang === 'hu' ? '🔴 Alap szint' : '🔴 Basic Level',
    levelYellow: lang === 'hu' ? '🟡 Középszint' : '🟡 Intermediate Level',
    levelGreen: lang === 'hu' ? '🟢 Jó szint' : '🟢 Good Level',
    environment: lang === 'hu' ? 'Környezet' : 'Environment',
    social: lang === 'hu' ? 'Társadalom' : 'Social',
    governance: lang === 'hu' ? 'Irányítás' : 'Governance',
    recommendations: lang === 'hu' ? 'Ajánlások' : 'Recommendations',
    qrTitle: lang === 'hu' ? 'Online frissíthető verzió' : 'Online Refreshable Version',
    qrDescription: lang === 'hu' ? 'Szkenneld be a QR kódot a legfrissebb jelentés megtekintéséhez' : 'Scan the QR code to view the latest report',
    footerReport: lang === 'hu' ? 'EVAT Mini ESG™ jelentés - Generálva' : 'EVAT Mini ESG™ Report - Generated',
    footerDescription: lang === 'hu' ? 'Ez a jelentés automatikusan generált, 15 kérdéses ESG értékelés alapján.' : 'This report was automatically generated based on a 15-question ESG assessment.'
  };

  // Use saved QR code URL if available, otherwise generate placeholder
  const finalQrCodeUrl = qrCodeUrl || (savedReportId 
    ? `${process.env.MINI_ESG_BASE_URL || "https://evat.esg"}/report/${savedReportId}`
    : `https://evat.esg/report/${companyName || 'company'}-${taxId || 'taxid'}-${Date.now()}`);
  
  let qrCodeDataUrl = '';
  
  try {
    qrCodeDataUrl = await QRCode.toDataURL(finalQrCodeUrl, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    // Continue without QR code if generation fails
  }

  // Create HTML content for PDF
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        @page {
          size: A4;
          margin: 20mm;
        }
        body {
          font-family: 'Arial', sans-serif;
          color: #000;
          line-height: 1.6;
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 3px solid ${brand.primary};
          padding-bottom: 16px;
          margin-bottom: 28px;
        }
        .logo {
          display: flex;
          align-items: center;
        }
        .logo img { height: 36px; }
        .brand-title { font-size: 18px; font-weight: bold; color: ${brand.primary}; margin-left: 10px; }
        .company-info {
          margin-bottom: 30px;
        }
        .score-section {
          margin: 30px 0;
          padding: 20px;
          border-radius: 8px;
        }
        .score-red { background: #ffdddd; border-left: 5px solid #ff4444; }
        .score-yellow { background: #fff7cc; border-left: 5px solid #ffcc00; }
        .score-green { background: #ddffdd; border-left: 5px solid #22aa22; }
        .score-total {
          font-size: 48px;
          font-weight: bold;
          text-align: center;
          margin: 20px 0;
        }
        .blocks {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          margin: 20px 0;
        }
        .block {
          padding: 15px;
          border-radius: 8px;
          background: #f5f5f5;
        }
        .block h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: bold;
        }
        .block-score {
          font-size: 32px;
          font-weight: bold;
        }
        .recommendations {
          margin-top: 30px;
          padding: 20px;
          background: #f9f9f9;
          border-radius: 8px;
        }
        .recommendations h3 {
          margin-top: 0;
        }
        .recommendations ul {
          margin: 10px 0;
          padding-left: 20px;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
        .contact {
          margin-top: 10px;
          font-size: 11px;
          color: #444;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">
          ${brand.logoUrl ? `<img src="${brand.logoUrl}" alt="Logo" />` : ''}
          <div class="brand-title">${brand.name}</div>
        </div>
        <div style="font-size: 13px; color: #666;">${labels.subtitle}</div>
      </div>

      <div class="company-info">
        <h2 style="margin: 0 0 10px 0;">${companyName || labels.unnamedCompany}</h2>
        <p style="margin: 0; color: #666;">${labels.taxIdLabel}: ${taxId || 'N/A'}</p>
        <p style="margin: 5px 0 0 0; color: #666;">${labels.dateLabel}: ${reportDate}</p>
      </div>

      <div class="score-section score-${score.level}">
        <div class="score-total" style="color: ${
          score.level === 'red' ? '#ff4444' : score.level === 'yellow' ? '#ffcc00' : '#22aa22'
        };">
          ${score.total} / 30
        </div>
        <div style="text-align: center; font-size: 18px; font-weight: bold; margin-top: 10px;">
          ${
            score.level === 'red' ? labels.levelRed :
            score.level === 'yellow' ? labels.levelYellow :
            labels.levelGreen
          }
        </div>
      </div>

      <div class="blocks">
        <div class="block" style="border-left: 4px solid #22aa22;">
          <h4>${labels.environment}</h4>
          <div class="block-score" style="color: #22aa22;">${score.environment} / 12</div>
        </div>
        <div class="block" style="border-left: 4px solid #0066cc;">
          <h4>${labels.social}</h4>
          <div class="block-score" style="color: #0066cc;">${score.social} / 10</div>
        </div>
        <div class="block" style="border-left: 4px solid #9933cc;">
          <h4>${labels.governance}</h4>
          <div class="block-score" style="color: #9933cc;">${score.governance} / 8</div>
        </div>
      </div>

      ${recommendations.length > 0 ? `
        <div class="recommendations">
          <h3>${labels.recommendations}</h3>
          <ul>
            ${recommendations.map(rec => `<li>${rec}</li>`).join('')}
          </ul>
        </div>
      ` : ''}

      ${qrCodeDataUrl ? `
        <div style="margin-top: 30px; text-align: center; padding: 20px; background: #f9f9f9; border-radius: 8px;">
          <h3 style="margin: 0 0 10px 0; font-size: 14px;">${labels.qrTitle}</h3>
          <img src="${qrCodeDataUrl}" alt="QR Code" style="width: 150px; height: 150px; margin: 10px auto; display: block;" />
          <p style="font-size: 11px; color: #666; margin-top: 10px;">${labels.qrDescription}</p>
        </div>
      ` : ''}

      <div class="footer">
        <p>${labels.footerReport}: ${new Date().toLocaleString(lang === 'hu' ? 'hu-HU' : 'en-US')}</p>
        <p style="font-size: 10px; color: #999;">${labels.footerDescription}</p>
        ${(brand.contactName || brand.contactEmail || brand.contactPhone) ? `
          <div class="contact">
            ${brand.contactName ? `<span>${brand.contactName}</span>` : ''}
            ${brand.contactEmail ? `<span> • ${brand.contactEmail}</span>` : ''}
            ${brand.contactPhone ? `<span> • ${brand.contactPhone}</span>` : ''}
          </div>
        ` : ''}
      </div>
    </body>
    </html>
  `;

  // Use html2pdf or similar library, or create a simple download
  // For now, we'll create a downloadable HTML file that can be printed to PDF
  const blob = new Blob([htmlContent], { type: 'text/html' });
  return blob;
}

export async function downloadMiniESGPDF() {
  try {
    const blob = await generateMiniESGPDF();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mini-esg-report-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to generate PDF:', error);
    alert('Failed to generate PDF. Please try again.');
  }
}

